import { aggregatorType, bucketType, chartGroupByOption, propertyType } from 'shared/constants.json';
import { equalP } from 'shared/lib/array/array';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { isEqual } from 'lodash-es';
import { textByLang } from 'shared/lib/language';

const addLeadingZero = (value) => value.toString().padStart(2, '0');

// Parse ISO date string as local date to avoid timezone issues
// new Date('2024-01-15') parses as UTC, causing day shifts in western timezones
const parseLocalDate = (dateString) => {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const getDayFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  return `${date.getFullYear()}-${addLeadingZero(date.getMonth() + 1)}-${addLeadingZero(date.getDate())}`;
};

export const getWeekFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  // ISO 8601: week 1 contains the first Thursday, so we find the nearest Thursday
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getFullYear()}-W${addLeadingZero(weekNumber)}`;
};

export const getMonthFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  return `${date.getFullYear()}-${addLeadingZero(date.getMonth() + 1)}`;
};

export const getYearFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  return date.getFullYear();
};

export const getQuarterFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `${date.getFullYear()}-Q${quarter}`;
};

export const edgeName = (groupBy, t, userLang) => {
  if (!groupBy.isProperty) {
    return t(`edges.${groupBy.edge}`);
  }
  return textByLang(groupBy.property.label, userLang);
};

export const roundNumber = (number, precision = 2) => Number(number.toFixed(precision));

export const isDateGrouping = (groupBy) => [
  chartGroupByOption.day,
  chartGroupByOption.week,
  chartGroupByOption.month,
  chartGroupByOption.quarter,
  chartGroupByOption.year,
].includes(groupBy);

const isEnumerableGrouping = (groupBy) => {
  if (groupBy?.groupBy !== chartGroupByOption.unique) {
    return false;
  }
  if (groupBy.isProperty === false) {
    return groupBy.edge === goalConfig.edges.goalCycle;
  }
  const { type } = groupBy.property ?? {};
  return [propertyType.space, propertyType.user, propertyType.status, propertyType.options, propertyType.singleSelect].includes(type);
};

export const canFillEmptyValues = (groupBy) => isDateGrouping(groupBy?.groupBy)
  || isEnumerableGrouping(groupBy);

export const fillMissingCategories = (categories, groupBy, userLang, excludeNullValues, context = {}) => {
  if (excludeNullValues) {
    return categories;
  }
  if (isDateGrouping(groupBy.groupBy)) {
    return fillMissingDateCategories(categories, groupBy, userLang);
  }
  if (isEnumerableGrouping(groupBy)) {
    return fillMissingEnumerableCategories(categories, groupBy, userLang, context);
  }
  return categories;
};

const fillMissingDateCategories = (categories, groupBy, userLang) => {
  if (categories.length < 2) {
    return categories;
  }

  const first = categories[0].originalValue;
  const last = categories[categories.length - 1].originalValue;
  const allDates = generateDateRange(first, last, groupBy.groupBy);
  const existingMap = new Map(categories.map((c) => [String(c.originalValue), c]));

  return allDates.map((date) => {
    const dateStr = String(date);
    if (existingMap.has(dateStr)) {
      return existingMap.get(dateStr);
    }
    return {
      title: getDisplayValue(date, undefined, groupBy, userLang),
      originalValue: date,
    };
  });
};

const fillMissingEnumerableCategories = (categories, groupBy, userLang, context) => {
  const allValues = getAllEnumerableValues(groupBy, userLang, context);
  if (allValues === null) {
    return categories;
  }
  return allValues;
};

const getAllEnumerableValues = (groupBy, userLang, context) => {
  const { goalCycles = [], spaces = [], users = [], leafStatuses = [], getColor = () => undefined } = context;

  if (groupBy.isProperty === false && groupBy.edge === goalConfig.edges.goalCycle) {
    return goalCycles.map((gc) => ({
      title: gc.title,
      originalValue: gc.uid,
      color: getColor(gc.color),
    }));
  }

  if (groupBy.isProperty === false) {
    return null;
  }

  if (groupBy.property?.type === propertyType.space) {
    return spaces.map((s) => ({
      title: s.title,
      originalValue: s.uid,
      color: getColor(s.color),
    }));
  }

  if (groupBy.property?.type === propertyType.user) {
    return users.map((u) => ({
      title: `${u.firstName} ${u.lastName}`,
      originalValue: u.uid,
      color: getColor(null),
    }));
  }

  if (groupBy.property?.type === propertyType.status) {
    return leafStatuses.map((opt) => ({
      title: textByLang(opt.label, userLang),
      originalValue: opt.uid,
      color: getColor(opt.color),
    }));
  }

  if (groupBy.property?.options) {
    return groupBy.property.options.map((opt) => ({
      title: textByLang(opt.label, userLang),
      originalValue: opt.uid,
      color: getColor(opt.color),
    }));
  }

  return null;
};

const resolveSingleValue = (value, groupBy, userLang, context) => {
  const { goalCycles = [], users = [], leafStatuses = [], spaces = [] } = context;

  const type = groupBy.property?.type;
  if (type === propertyType.space) {
    const space = spaces.find((s) => s.title === value);
    return space?.uid ?? null;
  }

  if (groupBy.isProperty === false && groupBy.edge === goalConfig.edges.goalCycle) {
    const uid = parseInt(value, 16);
    if (Number.isNaN(uid) || uid === 0) {
      return null;
    }
    const cycle = goalCycles.find((gc) => gc.uid === uid);
    return cycle?.uid ?? null;
  }

  if (type === propertyType.user) {
    const user = users.find((u) => `${u.firstName} ${u.lastName}` === value);
    return user?.uid ?? null;
  }

  if (type === propertyType.status) {
    const option = leafStatuses.find((opt) => textByLang(opt.label, userLang) === value);
    return option?.uid ?? null;
  }

  if (groupBy.property?.options?.length > 0) {
    const option = groupBy.property.options.find((opt) => textByLang(opt.label, userLang) === value);
    return option?.uid ?? null;
  }

  return value;
};

// Returns an array of resolved values (handles multi-value cases by expanding)
const resolveValueToArray = (value, groupBy, userLang, context) => {
  if (typeof value === 'string' && value.includes(CACHED_VALUE_SEPARATOR)) {
    const parts = value.split(CACHED_VALUE_SEPARATOR);
    const resolvedParts = parts.map((part) => resolveSingleValue(part, groupBy, userLang, context));
    // Filter out any parts that failed to resolve
    return resolvedParts.filter((p) => p !== null);
  }

  const resolved = resolveSingleValue(value, groupBy, userLang, context);
  return resolved !== null ? [resolved] : [];
};

const resolveResultData = (result, groupBy, stackOptions, userLang, context) => {
  const hasStackOptions = stackOptions?.groupBy !== chartGroupByOption.none;

  // Use flatMap to expand multi-value entries into separate items
  return result.flatMap((item) => {
    const resolvedVals = resolveValueToArray(item.val, groupBy, userLang, context);
    if (resolvedVals.length === 0) {
      return [];
    }

    if (!hasStackOptions) {
      return resolvedVals.map((val) => ({ ...item, val }));
    }

    const resolvedStackGroups = resolveValueToArray(item.stackGroup, stackOptions, userLang, context);
    if (resolvedStackGroups.length === 0) {
      return [];
    }

    // Create entries for each combination of val and stackGroup
    return resolvedVals.flatMap((val) => resolvedStackGroups.map((stackGroup) => ({ ...item, val, stackGroup })));
  });
};

export const cleanResult = (result, groupBy, stackOptions, userLang, context) => resolveResultData(result, groupBy, stackOptions, userLang, context);

const isEqualCustomBucket = (a, b) => {
  if (a.bucketType !== b.bucketType) {
    return false;
  }
  if (a.bucketType === bucketType.automatic) {
    return true;
  }
  if (a.bucketSize !== b.bucketSize) {
    return false;
  }
  if (a.min !== b.min) {
    return false;
  }
  return a.max === b.max;
};

const isEqualGroupBy = (a, b) => {
  if (a.groupBy !== b.groupBy) {
    return false;
  }
  if (a.groupBy === chartGroupByOption.none) {
    return true;
  }
  if (a.groupBy === chartGroupByOption.bucket && !isEqualCustomBucket(a.customBucket, b.customBucket)) {
    return false;
  }
  if (a.isProperty !== b.isProperty) {
    return false;
  }
  if (a.isProperty) {
    return a.property.uid === b.property.uid;
  }
  if (a.edge !== b.edge) {
    return false;
  }
  if (a.edgeType !== b.edgeType) {
    return false;
  }
  return true;
};

const isEqualAggregation = (a, b) => {
  if (a.aggregator !== b.aggregator) {
    return false;
  }
  if (a.aggregator === aggregatorType.count) {
    return true;
  }
  if (a.isProperty !== b.isProperty) {
    return false;
  }
  if (a.isProperty) {
    return a.property.uid === b.property.uid;
  }
  if (a.edge !== b.edge) {
    return false;
  }
  if (a.edgeType !== b.edgeType) {
    return false;
  }
  return true;
};

export const isEqualGridPageTileChart = (a, b) => {
  const equalUid = a.uid === b.uid;
  const equalChartType = a.chartType === b.chartType;
  const equalSource = a.source === b.source;
  const equalFilter = isEqual(a.filter, b.filter);
  const equalGoalCycles = equalP({ a: a.goalCycles, b: b.goalCycles });
  const equalGroupBy = isEqualGroupBy(a.groupBy, b.groupBy);
  const equalStackOptions = isEqualGroupBy(a.aggregationConfig.stackOptions, b.aggregationConfig.stackOptions);
  const equalAggregation = isEqualAggregation(a.aggregationConfig.aggregation, b.aggregationConfig.aggregation);
  const equalExcludeNullValues = a.excludeNullValues === b.excludeNullValues;
  const equalTimeSeriesInterval = a.aggregationConfig.timeSeriesInterval === b.aggregationConfig.timeSeriesInterval;
  return equalUid && equalChartType && equalSource && equalFilter && equalGoalCycles && equalGroupBy && equalStackOptions && equalAggregation && equalExcludeNullValues && equalTimeSeriesInterval;
};

export const generateDateRange = (start, end, groupBy) => {
  const result = [];

  switch (groupBy) {
    case chartGroupByOption.day: {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const current = new Date(startDate);
      while (current <= endDate) {
        result.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
      break;
    }
    case chartGroupByOption.week: {
      const [startYear, startWeekStr] = start.split('-W');
      const [endYear, endWeekStr] = end.split('-W');
      let currentYear = parseInt(startYear, 10);
      let currentWeek = parseInt(startWeekStr, 10);
      const finalYear = parseInt(endYear, 10);
      const finalWeek = parseInt(endWeekStr, 10);

      while (currentYear < finalYear || (currentYear === finalYear && currentWeek <= finalWeek)) {
        result.push(`${currentYear}-W${currentWeek.toString().padStart(2, '0')}`);
        currentWeek += 1;
        const weeksInYear = getISOWeeksInYear(currentYear);
        if (currentWeek > weeksInYear) {
          currentWeek = 1;
          currentYear += 1;
        }
      }
      break;
    }
    case chartGroupByOption.month: {
      const [startYear, startMonth] = start.split('-').map(Number);
      const [endYear, endMonth] = end.split('-').map(Number);
      let currentYear = startYear;
      let currentMonth = startMonth;

      while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
        result.push(`${currentYear}-${currentMonth.toString().padStart(2, '0')}`);
        currentMonth += 1;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear += 1;
        }
      }
      break;
    }
    case chartGroupByOption.quarter: {
      const [startYear, startQ] = start.split('-Q').map(Number);
      const [endYear, endQ] = end.split('-Q').map(Number);
      let currentYear = startYear;
      let currentQuarter = startQ;

      while (currentYear < endYear || (currentYear === endYear && currentQuarter <= endQ)) {
        result.push(`${currentYear}-Q${currentQuarter}`);
        currentQuarter += 1;
        if (currentQuarter > 4) {
          currentQuarter = 1;
          currentYear += 1;
        }
      }
      break;
    }
    case chartGroupByOption.year: {
      const startYear = typeof start === 'string' ? parseInt(start, 10) : start;
      const endYear = typeof end === 'string' ? parseInt(end, 10) : end;
      for (let year = startYear; year <= endYear; year += 1) {
        result.push(`${year}`);
      }
      break;
    }
    default:
      break;
  }

  return result;
};

// ISO 8601: years with 53 weeks start or end on Thursday
const getISOWeeksInYear = (year) => {
  const jan1 = new Date(year, 0, 1);
  const dec31 = new Date(year, 11, 31);
  return (jan1.getDay() === 4 || dec31.getDay() === 4) ? 53 : 52;
};

const CACHED_VALUE_SEPARATOR = '___COMMA___';

export const getDisplayValue = (cachedValue, entity, groupBy, userLang) => {
  if (isDateGrouping(groupBy.groupBy)) {
    switch (groupBy.groupBy) {
      case chartGroupByOption.day:
        return new Date(cachedValue).toLocaleDateString(userLang, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
      case chartGroupByOption.week: {
        // ISO 8601: week 1 contains the first Thursday, weeks start Monday
        const [year, weekStr] = cachedValue.split('-W');
        const week = parseInt(weekStr, 10);
        const yearStart = new Date(parseInt(year, 10), 0, 1);
        const dayOffset = yearStart.getDay() || 7;
        const mondayOffset = dayOffset <= 4 ? 1 - dayOffset : 8 - dayOffset;
        const firstMonday = new Date(yearStart);
        firstMonday.setDate(yearStart.getDate() + mondayOffset);
        const startDate = new Date(firstMonday);
        startDate.setDate(firstMonday.getDate() + (week - 1) * 7);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        return `${startDate.toLocaleDateString(userLang, { month: 'long', day: 'numeric' })} - ${endDate.toLocaleDateString(userLang, { month: 'long', day: 'numeric', year: 'numeric' })}`;
      }
      case chartGroupByOption.month:
        return new Date(cachedValue).toLocaleDateString(userLang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
      case chartGroupByOption.quarter: {
        const [year, quarter] = cachedValue.split('-Q');
        return `Q${quarter} ${year}`;
      }
      case chartGroupByOption.year:
        return new Date(cachedValue).toLocaleDateString(userLang, { year: 'numeric', timeZone: 'UTC' });
      default:
        return cachedValue;
    }
  }
  if (entity === undefined) {
    return cachedValue;
  }
  if (groupBy.isProperty === false && groupBy.edge === goalConfig.edges.goalCycle) {
    return entity.title;
  }
  if (groupBy.isProperty === false) {
    return cachedValue;
  }
  switch (groupBy.property.type) {
    case propertyType.user:
      return `${entity.firstName} ${entity.lastName}`;
    case propertyType.space:
      return entity.title;
    case propertyType.status:
    case propertyType.singleSelect:
    case propertyType.options:
      return textByLang(entity.label, userLang);
    default:
      return cachedValue;
  }
};
